type CopyToClipboardShareData = {
  title?: string;
  text?: string;
};

export type CopyToClipboardResult =
  | 'native-share'
  | 'clipboard'
  | 'exec-command'
  | 'cancelled'
  | false;

export const copyToClipboard = async (
  value: string,
  shareData?: CopyToClipboardShareData,
): Promise<CopyToClipboardResult> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    console.error('Copy/share failed: browser environment is not available');
    return false;
  }

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const canUseNativeShare = typeof navigator.share === 'function';

  // 1) Use native share API on mobile devices if available
  if (isMobile && canUseNativeShare) {
    try {
      await navigator.share({
        title: shareData?.title,
        text: shareData?.text,
        url: value,
      });
      return 'native-share';
    } catch (error) {
      // Dismissing the share sheet rejects with AbortError. That is the
      // visitor's answer, not a failure: copying instead would surprise
      // them, and with the tap's activation spent the copy fails too and
      // was reported to them as an error.
      if ((error as { name?: unknown } | null)?.name === 'AbortError') {
        return 'cancelled';
      }
      console.warn(
        'Native share failed, falling back to clipboard methods:',
        error,
      );
    }
  }

  // 2) Fallback to clipboard for desktop or if native share fails
  if (
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return 'clipboard';
    } catch (error) {
      console.warn('Clipboard API failed, falling back to execCommand:', error);
    }
  }

  // 3) Fallback method using document.execCommand (deprecated but more compatible)
  if (typeof document !== 'undefined') {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const isCopied = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (isCopied) return 'exec-command';
    } catch (error) {
      console.warn('execCommand fallback failed:', error);
    }
  }

  console.error('Copy/share failed: all available methods were unsuccessful');
  return false;
};
